import type { ReactNode } from "react";

/**
 * Application header — `role="banner"`, one per page.
 *
 * Slot-based so a designed layout drops in without changing this file:
 *  - `brand`   — tenant identity (logo slot + product name)
 *  - `actions` — user, theme toggle, and later: language, notifications, menu, logout
 *
 * ⚠ Colour rule (measured): shell text uses `--text`, which is AA-safe on every surface in both themes
 * (8.27–11.86). `--brand-interactive` is only guaranteed on the BASE `--surface` — on tinted surfaces Takeda's
 * drops to 3.54 and fails AA — so brand colour is for large/display text only. `--brand` is never text.
 */
export function AppHeader({ brand, actions }: { brand?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="border-border bg-background flex h-14 items-center justify-between gap-2 border-b px-3 sm:gap-4 sm:px-4">
      <div className="flex min-w-0 items-center gap-3">{brand}</div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
    </header>
  );
}

/**
 * Default brand block: the realm's logo (owner 2026-11-02) plus the tenant's product name. `productName` comes from the
 * edge-resolved bootstrap, so it is correct before first paint. Logos live in `public/logos/<realm>.png`, one per tenant
 * (asserted by a test). They are drawn for light backgrounds, so on dark they sit on a white chip.
 */
export function BrandMark({ productName, realm, clientName }: { productName: string; realm: string; clientName: string }) {
  return (
    <>
      <img
        src={`/logos/${realm}.png`}
        alt={clientName}
        className="h-9 w-auto max-w-28 shrink-0 object-contain dark:rounded-md dark:bg-white dark:p-0.5"
      />
      {/* Below `sm` the actions need the room: the tenant name stays for screen readers only. */}
      <span aria-hidden="true" className="bg-border h-5 w-px shrink-0 max-sm:hidden" />
      <span className="truncate font-semibold max-sm:sr-only">{productName}</span>
    </>
  );
}
